import { supabase } from '@/lib/supabase';
import { mapWithConcurrency } from '@/lib/concurrency';
import type { QuestionRubric, ExtractedQuestion } from '@/types/pipeline';

type ModelConfig = {
  provider: string;
  model: string;
};

type RubricSuggestInput = Pick<
  ExtractedQuestion,
  'questionId' | 'sectionInstruction' | 'questionText' | 'questionType'
>;

/**
 * Suggest rubrics for a batch of questions.
 * Canonical implementation from NewAssignmentPage with improved error handling from DevModelEvalPage.
 */
export async function suggestRubricsBatch(
  questions: RubricSuggestInput[],
  gradeLevel: string,
  /** Omit to use the admin panel setting (the normal path). */
  modelConfig: ModelConfig | undefined,
  onProgress?: (batch: number, totalBatches: number, message: string) => void,
): Promise<QuestionRubric[]> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  const headers = {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : '',
  };

  const BATCH_SIZE = 5;
  const totalBatches = Math.ceil(questions.length / BATCH_SIZE);
  const batches = Array.from({ length: totalBatches }, (_, i) =>
    questions.slice(i * BATCH_SIZE, Math.min((i + 1) * BATCH_SIZE, questions.length)),
  );

  // Batches run concurrently (bounded); the old 500ms inter-batch sleep predates
  // OpenRouter routing and is gone.
  let completedBatches = 0;
  onProgress?.(0, totalBatches, `產生評分標準（${totalBatches} 批）…`);
  const perBatchRubrics = await mapWithConcurrency(batches, 3, async (batch, batchIdx) => {
    const response = await fetch('/api/dev/model-eval/grade-paper/suggest-rubrics', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        questions: batch.map(({ questionId, sectionInstruction, questionText, questionType }) => ({
          questionId,
          sectionInstruction,
          questionText,
          questionType,
        })),
        gradeLevel,
        // omitted → server uses the admin panel's 評分準則建議 setting
        ...(modelConfig ? { modelConfig } : {}),
      }),
    });

    if (!response.ok) {
      const raw = await response.text();
      // Surface specific error hints from the server (truncation, rate limits, connection errors)
      if (response.status === 429 || response.status === 503 || response.status === 400) {
        let msg =
          response.status === 503
            ? '與模型服務連線失敗，請稍後再試。'
            : response.status === 400
              ? '模型輸出被截斷或格式錯誤。'
              : '請求過於頻繁（429），請稍後再試。';
        try {
          const j = JSON.parse(raw) as { message?: string; hint?: string };
          msg = j.hint || j.message || msg;
        } catch {
          if (raw.trim()) msg = raw;
        }
        throw new Error(`批次 ${batchIdx + 1} 失敗: ${msg}`);
      }
      throw new Error(`批次 ${batchIdx + 1} 失敗: ${raw}`);
    }

    const batchData = await response.json();
    completedBatches++;
    onProgress?.(completedBatches, totalBatches, `已完成 ${completedBatches}/${totalBatches} 批評分標準…`);
    return (batchData.rubrics || []) as QuestionRubric[];
  });

  // Flatten in batch order so rubric order matches question order
  return perBatchRubrics.flat();
}

/**
 * Suggest a rubric for a single question.
 * Canonical implementation from NewAssignmentPage.
 */
export async function suggestSingleRubric(
  question: RubricSuggestInput,
  gradeLevel: string,
  /** Omit to use the admin panel setting (the normal path). */
  modelConfig: ModelConfig | undefined,
): Promise<QuestionRubric | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  const response = await fetch('/api/dev/model-eval/grade-paper/suggest-rubrics', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    },
    body: JSON.stringify({
      questions: [
        {
          questionId: question.questionId,
          sectionInstruction: question.sectionInstruction,
          questionText: question.questionText,
          questionType: question.questionType,
        },
      ],
      gradeLevel,
      // omitted → server uses the admin panel's 評分準則建議 setting
      ...(modelConfig ? { modelConfig } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const data = await response.json();
  return (data.rubrics || [])[0] ?? null;
}
